<template>
  <v-container fluid pa-0>
    <v-row>
      <!-- Date range -->
      <v-col cols="12">
        <helpers-section-sub-header>Date Range</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          The historical period the backtest will simulate. A longer date range gives more data
          points and a better estimate of long-run performance, but may include market regimes
          very different from today. Leave the end date blank to run through the most recent
          available data.
        </p>
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <v-menu :close-on-content-click="false" transition="scale-transition" min-width="290px">
              <template #activator="{ props }">
                <v-text-field
                  :model-value="formatDate(criteria.general.start_date)"
                  label="Start date"
                  :prepend-icon="mdiCalendar"
                  :error-messages="criteria.general.start_date ? [] : ['Start date is required']"
                  readonly
                  variant="outlined"
                  density="compact"
                  v-bind="props"
                />
              </template>
              <v-date-picker v-model="criteria.general.start_date" no-title scrollable @update:model-value="criteria.general.start_date = formatDate($event)" />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="4" xl="3">
            <v-menu :close-on-content-click="false" transition="scale-transition" min-width="290px">
              <template #activator="{ props }">
                <v-text-field
                  :model-value="formatDate(criteria.general.end_date)"
                  placeholder=" "
                  label="End date (leave blank for most recent data)"
                  :prepend-icon="mdiCalendar"
                  hide-details
                  clearable
                  readonly
                  variant="outlined"
                  density="compact"
                  v-bind="props"
                  @click:clear="criteria.general.end_date = null"
                />
              </template>
              <v-date-picker v-model="criteria.general.end_date" no-title scrollable @update:model-value="criteria.general.end_date = formatDate($event)" />
            </v-menu>
          </v-col>
        </v-row>
      </v-col>

      <!-- Starting amount -->
      <v-col cols="12">
        <helpers-section-sub-header>Starting Amount</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          The hypothetical portfolio value at the beginning of the backtest. All position sizes,
          returns, and performance metrics are calculated relative to this amount. The absolute
          dollar value does not affect percentage-based results, but it does affect position sizing
          when minimum or maximum dollar thresholds are configured.
        </p>
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <v-text-field
              v-model="criteria.general.starting_amount"
              type="number"
              prefix="$"
              variant="outlined"
              density="compact"
              label="Initial portfolio value"
              class="pt-0"
              :error-messages="parseFloat(criteria.general.starting_amount) > 0 ? [] : ['Starting amount must be greater than 0']"
            />
          </v-col>
        </v-row>
      </v-col>

      <!-- Performance Comparison -->
      <v-col cols="12">
        <helpers-section-sub-header>Performance Comparison</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          Choose a benchmark to compare your model's returns against in the results charts and
          tables. The <strong>S&amp;P 500 Total Return Index</strong> (which includes dividends) is
          the standard benchmark for equity strategies. You can also compare against any individual
          stock or ETF — useful for testing whether your model beats a simple buy-and-hold of a
          relevant index like QQQ or IWM.
        </p>
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <v-autocomplete
              v-model="criteria.general.comp_index"
              v-model:search-input="searchInput"
              :items="searchResults"
              :loading="loading"
              :hide-no-data="!searchInput || searchInput.length < 1 || loading"
              no-data-text="No such stock found"
              return-object
              item-title="description"
              item-value="id"
              variant="outlined"
              density="compact"
              :prepend-inner-icon="mdiMagnify"
              hide-details
              label="Search for benchmark stock or index"
              class="pt-0"
              @update:search="querySearch"
              @update:model-value="clearSearch"
            />
          </v-col>
          <v-col cols="12">
            <span v-if="!criteria.general.comp_index" class="text-body-2 text-disabled">
              No comparison benchmark selected
            </span>
            <template v-else>
              <v-chip
                small
                label
                close
                color="primary text-white"
                class="ma-1"
                @update:active="criteria.general.comp_index = null"
              >
                {{ criteria.general.comp_index.description }}
              </v-chip>
            </template>
          </v-col>
        </v-row>
      </v-col>

      <!-- Shorting Margin Options -->
      <v-col v-if="short" cols="12">
        <helpers-section-sub-header>Shorting Margin Options</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          When your model includes short signals, the backtester enforces a margin requirement.
          If a short position moves against you and the account equity falls below the maintenance
          margin percentage, a forced buy-to-cover is simulated at that day's close. A typical
          broker requirement is <strong>25–30%</strong>. Setting a higher value forces earlier
          exits, reducing risk of catastrophic loss on runaway shorts.
        </p>
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <v-text-field
              v-model="criteria.general.short_maintenance_margin"
              type="number"
              variant="outlined"
              density="compact"
              label="Maintenance margin % (forced cover threshold)"
              suffix="%"
              hide-details
              class="pt-0"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12">
        <helpers-section-sub-header>Email Notifications</helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          Receive a daily email each trading morning with your model's updated performance and any
          trades that were triggered the previous day. Useful for monitoring a live model without
          logging in daily.
        </p>
        <v-checkbox
          v-model="current_trading_model.alert_enabled"
          density="compact"
          label="Send daily email with updated performance and trades"
          hint="Sent each trading morning. WhaleWisdom makes no guarantee of email delivery."
          persistent-hint
        />
      </v-col>
      <!--
      <v-col cols="12">
        <helpers-section-sub-header>13F Backtester Override</helpers-section-sub-header>
        <v-checkbox
          v-model="criteria.f13_override"
          density="compact"
          label="13F Override"
          hide-details
        />
      </v-col>
      -->
    </v-row>
    <!-- Slippage % Rate -->
    <helpers-section-sub-header class="mt-6">Slippage Rate</helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      Slippage simulates the difference between the theoretical closing price and the actual
      execution price. Real-world trades rarely fill at the exact closing price — especially for
      less liquid stocks or larger position sizes. The slippage rate scales the intraday price
      range (close minus low) and adjusts the simulated execution price accordingly. A rate of
      <strong>0%</strong> assumes perfect fills at the close. A rate of <strong>25–50%</strong>
      is a reasonable real-world estimate for most strategies.
    </p>
    <v-row no-gutters align="center">
      <v-checkbox
        v-model="criteria.general.slippage_enabled"
        hide-details
        density="compact"
        label="Enable slippage"
        class="shrink mr-2"
      />
      <v-text-field
        v-model="criteria.general.slippage_rate"
        :disabled="!criteria.general.slippage_enabled"
        type="number"
        suffix="%"
        variant="outlined"
        density="compact"
        label="Slippage rate"
        class="shrink"
        style="max-width: 150px"
        hide-details
      />
      <v-col cols="12" class="mt-4">
        <p class="text-body-2 mb-1"><strong>Example</strong> with slippage rate <code>25%</code>:</p>
        <div class="text-body-2 mb-1">Closing price: <code>$100</code> &nbsp;|&nbsp; Low for day: <code>$98</code></div>
        <div class="text-body-2 mb-1">Slippage adjustment: <code>(100 − 98) × 0.25 = $0.50</code></div>
        <div class="text-body-2">Execution price used in backtest: <code>$100 − $0.50 = $99.50</code></div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// Modules
import { mapFields } from '@/store/helpers'
import AccessMixin from '@/mixins/access'
import api from '@/store/api'
import { mdiCalendar, mdiMagnify } from '@mdi/js'
export default {
  mixins: [AccessMixin],
  data() {
    return {
      mdiCalendar,
      mdiMagnify,
      short: false,
      loading: false,
      // stocksProxy: null,
      searchInput: null,
      searchResults: [],
      spy_tr_index: {
        name: 'S&P 500 Total Return Index',
        id: '169819',
        permalink: 'spyb',
        ticker: ['SP500TR'],
        rectype: 'stock',
        description: 'S&P 500 Total Return Index'
      }
    }
  },
  computed: {
    ...mapFields({
      criteria: 'app.whaleTraderCriteria',
      current_trading_model: 'app.current_trading_model'
    })
  },
  watch: {
    searchInput(val) {
      val && this.querySearch(val)
      this.searchInput = null
    }

    /* stocksProxy (stocksArray) {
          this.$emit('input',stocksArray);
        //this.$emit('update:stocks', stocksArray)
      } */
  },
  created() {
    this.searchResults.push(this.spy_tr_index)
    //   this.stocksProxy=this.criteria.general.comp_index;
  },
  methods: {
    formatDate(val) {
      if (!val) return val
      if (val instanceof Date) return val.toISOString().slice(0, 10)
      return String(val).slice(0, 10)
    },
    formatName(item) {
      if (!item) {
        return ''
      }
      return item.description
    },
    matchall(val) {
      return true
    },
    querySearch(val) {
      if (!val || val.length == 0 || val=="S&P 500 Total Return Index") return
      this.loading = true
      const query = `stock_autocomplete?restrictions=${this.filterInput}&keyword=${val}`
      api
        .get(query)
        .then(res => {
          this.searchResults = []
          if (res.data && res.data.length > 0){
 for (var i = 0; i < res.data.length; i++) {
            var item = res.data[i]
            this.searchResults.push({ id: item.id, description: item.ticker[0] + ' ' + item.name })
          }
          } 
          else this.searchResults = []
         
          // this.searchResults = res.data
          this.searchResults.push(this.spy_tr_index)

        })
        .then(() => (this.loading = false))
    },
    clearSearch() {
      // This method is called when a selection is made
      this.searchInput = ''
    },
  }
}
</script>
